import { FirebaseError } from 'firebase/app';

const FUNCTION_ERROR_MESSAGES: Record<string, string> = {
  'functions/cancelled': 'The request was cancelled. Please try again.',
  'functions/unknown': 'Something went wrong. Please try again.',
  'functions/internal': 'Something went wrong on the server. Please try again.',
  'functions/unavailable': 'The service is temporarily unavailable. Please try again in a moment.',
  'functions/deadline-exceeded': 'The request timed out. Please try again.',
  'functions/not-found': 'This feature is not available yet. Contact an admin if the problem continues.',
  'functions/permission-denied': 'You do not have permission to do that.',
  'functions/unauthenticated': 'Sign in again to continue.',
  'functions/failed-precondition': 'This action cannot be completed right now.',
  'functions/aborted': 'The request was interrupted. Please try again.',
  'functions/already-exists': 'This record already exists.',
  'functions/invalid-argument': 'Some of the information provided is invalid. Check the form and try again.',
  'functions/resource-exhausted': 'Too many requests. Please wait a moment and try again.',
};

const TECHNICAL_MESSAGE_TOKENS = new Set([
  'internal',
  'unknown',
  'unavailable',
  'cancelled',
  'aborted',
  'deadline-exceeded',
  'not-found',
  'permission-denied',
  'unauthenticated',
  'failed-precondition',
  'already-exists',
  'invalid-argument',
  'resource-exhausted',
]);

function codeWithoutFunctionsPrefix(code: string) {
  return code.replace(/^functions\//, '').replace(/-/g, ' ');
}

function isTechnicalMessage(message: string, code?: string) {
  const trimmed = message.trim();
  if (!trimmed) {
    return true;
  }

  if (trimmed.startsWith('functions/')) {
    return true;
  }

  const normalized = trimmed.toLowerCase();
  if (TECHNICAL_MESSAGE_TOKENS.has(normalized)) {
    return true;
  }

  if (code) {
    if (trimmed === code) {
      return true;
    }

    if (normalized === codeWithoutFunctionsPrefix(code).toLowerCase()) {
      return true;
    }
  }

  return false;
}

/**
 * Returns a safe message for UI display. Never surfaces raw Firebase codes like "internal".
 */
export function formatUserFacingError(error: unknown, fallback: string) {
  if (error instanceof FirebaseError) {
    const message = error.message?.trim();
    if (message && !isTechnicalMessage(message, error.code)) {
      return message;
    }

    const mapped = FUNCTION_ERROR_MESSAGES[error.code];
    if (mapped) {
      return mapped;
    }

    return fallback;
  }

  if (error instanceof Error) {
    const message = error.message?.trim();
    if (message && !isTechnicalMessage(message)) {
      return message;
    }
  }

  if (error && typeof error === 'object' && 'message' in error) {
    const message = String((error as { message?: unknown }).message ?? '').trim();
    if (message && !isTechnicalMessage(message)) {
      return message;
    }
  }

  return fallback;
}
